import { nanoid } from 'nanoid';
import React, { useContext } from 'react';
import { ClickToDismissPortalContext } from '../popover/context';

/** Allows inferring of arguments from a generic type */
export type InferArgs<T> = T extends (...t: [...infer Arg]) => any ? Arg : never;

/** A void returning function that has any arguments */
export type AnyFunc = (...args: any[]) => void;

/** Hook for a child element of MUI popovers.
 * Something what have a popover functionality but cannot have other children
 */
export function useMuiClickToDismiss<TOpen extends AnyFunc, TClose extends AnyFunc>(open?: boolean, handleOnClose?: TClose, handleOnOpen?: TOpen) {
	const context = useContext(ClickToDismissPortalContext);
	const openedChildIds = React.useRef<Record<string, boolean | (() => boolean)>>({});
	const [internalVis, setInternalVis] = React.useState(open !== undefined ? open : false);
	const popoverId = React.useRef(nanoid());
	const childPortalOpened = (id, dontBlockClose) => {
		openedChildIds.current[id] = dontBlockClose;
		if (context) context.childPortalOpened(id);
	};
	const childPortalClosed = id => {
		context?.childPortalClosed(id);

		setTimeout(() => {
			delete openedChildIds.current[id];
		}, 100);
	};
	const isOpen = open !== undefined ? open : internalVis;
	const onClose = (...args: InferArgs<TClose>) => {
		if (Object.keys(openedChildIds.current).length === 0) {
			context.childPortalClosed(popoverId.current);
			if (handleOnClose) {
				handleOnClose(...args);
			}

			setInternalVis(false);
		}
	};
	const onOpen = (...args: InferArgs<TOpen>) => {
		context.childPortalOpened(popoverId.current);

		if (handleOnOpen) {
			handleOnOpen(...args);
		}

		setInternalVis(true);
	};
	return {
		context,
		contextValue: {
			childPortalOpened,
			childPortalClosed
		},
		isOpen,
		onOpen,
		onClose
	};
}
